import { Component, computed, inject, input } from "@angular/core";
import { EvDialogModule } from "ev-dialog";
import { SaleInArea } from "src/models/estate/IResidenceSaleInArea";
import { FilterOverview, FilterType } from "src/models/shared/filter.model";
import { FavoriteListDataService } from "src/services/estate/favorite-list-data.service";
import { FavoriteListExportService } from "src/services/estate/favorite-list-export.service";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { FavoriteListStates, OverviewFavoriteListsStore } from "src/stores/overview-favorite-lists.store";
import { SelectedFavoriteListsStore } from "src/stores/selected-favorite-list.store";
import { FavoriteListConfirmCloseComponent } from "./favorite-list-confirm-close/favorite-list-confirm-close.component";
import { FavoriteListMenuComponent } from "./favorite-list-menu/favorite-list-menu.component";
import { FavoriteListThumbComponent } from "./favorite-list-thumb/favorite-list-thumb.component";
import { FavoriteListComponent } from "./favorite-list/favorite-list.component";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";

@Component({
    selector: "estate-favorite-lists",
    templateUrl: "./estate-favorite-lists.component.html",
    styleUrl: "./estate-favorite-lists.component.scss",
    imports: [FavoriteListMenuComponent, FavoriteListComponent, FavoriteListConfirmCloseComponent, FavoriteListThumbComponent, EvDialogModule],
})
export class EstateFavoriteListsComponent {
    sales = input.required<SaleInArea[]>();
    readonly overviewFavoriteListsStore = inject(OverviewFavoriteListsStore);
    readonly selectedFavoriteListStore = inject(SelectedFavoriteListsStore);
    readonly favoriteListDataService = inject(FavoriteListDataService);
    readonly favoriteListExportService = inject(FavoriteListExportService);
    readonly rightSidebarService = inject(RightSidebarService);
    readonly mapStateService = inject(MapStateService);

    FavoriteListStates = FavoriteListStates;
    hasFavoriteListSaveRight: boolean = false;
    isExpanded: boolean = false;
    showDialog: boolean = true;
    showConfirmDialog: boolean = false;
    showConfirmCloseBeforeSave: boolean = false;
    confirmDeleteId: number = null;
    mapMode: MapMode;

    isInOverviewState = computed(() => this.overviewFavoriteListsStore.state() === FavoriteListStates.Overview);
    isInFavoriteOpenState = computed(
        () => this.overviewFavoriteListsStore.state() === FavoriteListStates.Details || this.overviewFavoriteListsStore.state() === FavoriteListStates.NewCart,
    );
    isInNewFavoriteState = computed(() => this.overviewFavoriteListsStore.state() === FavoriteListStates.NewCart);
    hasFavoriteLists = computed(() => this.overviewFavoriteListsStore.overview()?.length);
    favoriteListContent = computed(() => this.selectedFavoriteListStore.sales());
    listEstateDescription = computed(() => (this.overviewFavoriteListsStore.filterType() === FilterType.NewDwelling ? "prosjekter" : "boliger"));
    listHeaderTypeName = computed(() => {
        const type = this.overviewFavoriteListsStore.filterType();
        if (type === FilterType.Market) return "Salg";
        if (type === FilterType.Properties) return "Boliger";
        if (type === FilterType.NewDwelling) return "Nybygg";
        return "Salg";
    });

    openFavorite(favoriteId: number): void {
        this.forceCloseMenues();
        this.favoriteListDataService.loadFavoriteList(favoriteId);
    }
    closeFavorite(): void {
        if (this.selectedFavoriteListStore.changed()) {
            this.showConfirmCloseBeforeSave = true;
        } else {
            this.forceCloseMenues();
            this.favoriteListDataService.closeCurrentFavoriteList();
        }
    }

    renameFavorite(favoriteId: number): void {
        this.closeMenu(favoriteId);
        if (favoriteId === -1 || this.selectedFavoriteListStore.id() === favoriteId) {
            this.selectedFavoriteListStore.updateEditName(true);
        } else {
            this.favoriteListDataService.loadFavoriteList(favoriteId, true);
        }
    }
    newFavoriteList(prefill: boolean = false): void {
        this.favoriteListDataService.newFavoriteList(prefill);
    }
    deleteFavorite(favoriteId: number, overrideConfirm: boolean = false): void {
        this.overviewFavoriteListsStore.setSelected(favoriteId);
        if (overrideConfirm) {
            this.favoriteListDataService.deleteFavoriteList(favoriteId);
        } else {
            this.confirmDeleteId = favoriteId;
            this.showConfirmDialog = true;
        }
        this.closeMenu(favoriteId);
    }
    saveFavoriteList(): void {
        if (this.selectedFavoriteListStore.canSave()) {
            this.favoriteListDataService.saveFavoriteList();
        }
    }

    closeDialog(): void {
        this.showConfirmDialog = false;
        this.showConfirmCloseBeforeSave = false;
        this.confirmDeleteId = null;
    }

    confirmDialogVerification(): void {
        this.forceCloseMenues();
        if (this.showConfirmDialog) {
            this.favoriteListDataService.deleteFavoriteList(this.confirmDeleteId);
        } else if (this.showConfirmCloseBeforeSave) {
            this.favoriteListDataService.closeCurrentFavoriteList();
        }
        this.closeDialog();
    }

    openMenu(favoriteId: number): void {
        this.overviewFavoriteListsStore.setActiveMenu(favoriteId);
    }
    closeMenu(favoriteId: number): void {
        if (this.overviewFavoriteListsStore.activeMenu() === favoriteId) {
            this.overviewFavoriteListsStore.closeMenu();
        }
    }

    forceCloseMenues(): void {
        this.overviewFavoriteListsStore.closeMenu();
    }

    addAllSelection(favoriteId: number): void {
        this.favoriteListDataService.addShortSales();
        this.closeMenu(favoriteId);
    }

    removeAllSelection(favoriteId: number): void {
        this.favoriteListDataService.removeShortSales();
        this.closeMenu(favoriteId);
    }

    gotoListMapBounds(favoriteId: number): void {
        const polygons = this.selectedFavoriteListStore.polygon();
        if (this.overviewFavoriteListsStore.state() === FavoriteListStates.Overview) {
            this.favoriteListDataService.loadFavoriteList(favoriteId);
        } else if (this.selectedFavoriteListStore.content().length > 0) {
            this.favoriteListDataService.centerMapOnSales(this.selectedFavoriteListStore.content(), polygons);
        }
        this.closeMenu(favoriteId);
    }

    exportSelectionToPdf(favorite: FilterOverview): void {
        this.favoriteListExportService.exportSelectionToPdf(favorite);
        this.closeMenu(favorite?.id);
    }
    exportSelectionToExcel(favorite: FilterOverview): void {
        this.favoriteListExportService.exportSelectionToExcel(favorite);
        this.closeMenu(favorite?.id);
    }

    exportLongSaleToExcel(): void {
        this.favoriteListExportService.exportLongSaleToExcel(this.sales());
    }
    exportLongSaleToPdf(): void {
        this.favoriteListExportService.exportLongSaleToPdf(this.sales());
    }

    onCloseSideBar(): void {
        this.rightSidebarService.toggleSidebarVisibility(RightSidebarOptions.Closed);
    }
}
